import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

/**
 * Modern Companies Page
 * Beautiful grid layout with search, filters, and smooth animations
 */
const CompaniesPage = ({ user }) => {
  const navigate = useNavigate();
  const isManager = user.role === 'Manager' || user.role === 'Admin';
  
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressByCompanyId, setProgressByCompanyId] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/companies');
      const data = res.data.data || res.data;
      setCompanies(data);
      setFilteredCompanies(data);
    } catch (e) {
      console.error('Error fetching companies:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Fetch onboarding progress (single source of truth) for each company
  useEffect(() => {
    let cancelled = false;

    async function loadProgress() {
      if (!companies.length) {
        setProgressByCompanyId({});
        return;
      }

      // Batch to avoid bursts
      const batchSize = 5;
      const result = {};

      for (let i = 0; i < companies.length; i += batchSize) {
        const batch = companies.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (c) => {
            try {
              const res = await api.get(`/reports/company/${c.id}/preview`);
              const pct = res.data?.progress?.percentage;
              if (typeof pct === 'number') {
                result[c.id] = {
                  percentage: pct,
                  completed: res.data?.progress?.completed ?? null,
                  total: res.data?.progress?.total ?? null,
                };
              }
            } catch (e) {
              // fallback to old UI logic if preview fails
            }
          })
        );
      }

      if (!cancelled) {
        setProgressByCompanyId(result);
      }
    }

    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [companies]);

  // Filter and sort companies
  useEffect(() => {
    let result = [...companies];
    
    // Search filter
    if (searchQuery) {
      result = result.filter(c => 
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.contactPerson?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'branches':
          return (b.Branches?.length || 0) - (a.Branches?.length || 0);
        case 'recent':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });
    
    setFilteredCompanies(result);
  }, [companies, searchQuery, sortBy]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">Companies</h1>
              <p className="text-gray-600 dark:text-slate-400">
                Manage your client companies and their onboarding progress
              </p>
            </div>
            
            {isManager && (
              <button
                onClick={() => navigate('/journey')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 rounded-xl text-white font-semibold transition-all duration-200 shadow-lg shadow-blue-500/25 flex items-center gap-2"
              >
                <span className="text-xl">+</span>
                Add Company
              </button>
            )}
          </div>
        </header>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 p-4 mb-6 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400">🔍</span>
              <input
                type="text"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
              />
            </div>
            
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer"
            >
              <option value="name" className="bg-white dark:bg-slate-800">Sort by Name</option>
              <option value="branches" className="bg-white dark:bg-slate-800">Sort by Branches</option>
              <option value="recent" className="bg-white dark:bg-slate-800">Most Recent</option>
            </select>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MiniStat label="Total Companies" value={companies.length} />
          <MiniStat label="Total Branches" value={companies.reduce((sum, c) => sum + (c.Branches?.length || 0), 0)} />
          <MiniStat
            label="Active Onboarding"
            value={companies.filter(c => {
              const pct = progressByCompanyId[c.id]?.percentage;
              if (typeof pct === 'number') return pct > 0 && pct < 100;
              return c.Branches?.some(b => !b.onboardingComplete);
            }).length}
          />
          <MiniStat
            label="Completed"
            value={companies.filter(c => {
              const pct = progressByCompanyId[c.id]?.percentage;
              if (typeof pct === 'number') return pct >= 100;
              return c.Branches?.every(b => b.onboardingComplete);
            }).length}
          />
        </div>

        {/* Companies Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/10 p-6 animate-pulse">
                <div className="w-12 h-12 bg-gray-300 dark:bg-slate-700 rounded-xl mb-4" />
                <div className="h-6 bg-gray-300 dark:bg-slate-700 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-300 dark:bg-slate-700 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredCompanies.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company, idx) => (
              <CompanyCard 
                key={company.id} 
                company={company} 
                progressOverride={progressByCompanyId[company.id]?.percentage}
                onClick={() => navigate(`/company/${company.id}`)}
                delay={idx * 50}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {searchQuery ? 'No companies found' : 'No companies yet'}
            </h3>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : 'Get started by creating your first company'
              }
            </p>
            {!searchQuery && isManager && (
              <button
                onClick={() => navigate('/journey')}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-semibold transition-colors"
              >
                Create Company
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Mini Stat Component
const MiniStat = ({ label, value }) => (
  <div className="bg-white dark:bg-white/5 backdrop-blur-xl rounded-xl border border-gray-200 dark:border-white/10 p-4 shadow-sm">
    <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
    <div className="text-gray-600 dark:text-slate-400 text-sm">{label}</div>
  </div>
);

// Company Card Component
const CompanyCard = ({ company, onClick, delay, progressOverride }) => {
  const branchCount = company.Branches?.length || 0;
  const completedBranches = company.Branches?.filter(b => b.onboardingComplete).length || 0;
  const fallbackProgress = branchCount > 0 ? Math.round((completedBranches / branchCount) * 100) : 0;
  const progress = typeof progressOverride === 'number' ? progressOverride : fallbackProgress;
  
  // Generate a consistent color based on company name
  const colors = ['blue', 'violet', 'emerald', 'amber', 'rose', 'cyan'];
  const colorIndex = company.name?.charCodeAt(0) % colors.length || 0;
  const gradients = {
    blue: 'from-blue-500 to-blue-600',
    violet: 'from-violet-500 to-violet-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    rose: 'from-rose-500 to-rose-600',
    cyan: 'from-cyan-500 to-cyan-600',
  };

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 p-6 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group shadow-sm"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${gradients[colors[colorIndex]]} rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
          {company.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <span className="text-gray-400 dark:text-slate-500 group-hover:text-blue-600 dark:group-hover:text-white transition-colors text-xl">→</span>
      </div>

      {/* Company Info */}
      <h3 className="text-gray-900 dark:text-white font-semibold text-lg mb-1 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
        {company.name}
      </h3>
      {company.contactPerson && (
        <p className="text-gray-600 dark:text-slate-400 text-sm mb-4 truncate">
          Contact: {company.contactPerson}
        </p>
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 dark:text-slate-500">🏢</span>
          <span className="text-gray-700 dark:text-slate-300 text-sm">{branchCount} branches</span>
        </div>
        {company.registrationNumber && (
          <div className="flex items-center gap-2">
            <span className="text-gray-400 dark:text-slate-500">#</span>
            <span className="text-gray-700 dark:text-slate-300 text-sm truncate">{company.registrationNumber}</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-gray-600 dark:text-slate-400">Onboarding Progress</span>
          <span className="text-gray-900 dark:text-white font-medium">{progress}%</span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${gradients[colors[colorIndex]]} rounded-full transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default CompaniesPage;
